// src/products/dataroom/components/SetupWizard/UserInvitationStep.js
import React, { useState, useEffect } from 'react';
import { Plus, User, Mail, Trash2, CheckCircle, ChevronDown, ChevronRight } from 'lucide-react';
// import partyTeamService from '../../services/partyTeamService';
import { supabase } from '../../../../config';

const UserInvitationStep = ({ projectId, setupData, onComplete }) => {
  const [userAssignments, setUserAssignments] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showEmailForm, setShowEmailForm] = useState(null); // {partyId, teamId}
  const [newUserEmail, setNewUserEmail] = useState('');
  const [expandedParty, setExpandedParty] = useState(null); // Only one party can be expanded at a time

  useEffect(() => {
    // Initialize user assignments structure
    if (setupData.teams) {
      const initialAssignments = {};
      const partyIds = Object.keys(setupData.teams);
      
      partyIds.forEach(partyId => {
        initialAssignments[partyId] = {};
        setupData.teams[partyId].forEach(team => {
          initialAssignments[partyId][team.id] = [];
        });
      });
      
      setUserAssignments(initialAssignments);
      
      // Expand first party by default
      if (partyIds.length > 0) {
        setExpandedParty(partyIds[0]);
      }
    }
  }, [setupData.teams]);

  const togglePartyExpansion = (partyId) => {
    setExpandedParty(expandedParty === partyId ? null : partyId);
  };


  const handleAddUserInline = (partyId, teamId, email) => {
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    // Check if email already exists in this team
    const existingUser = userAssignments[partyId]?.[teamId]?.find(u => u.email === email);
    if (existingUser) {
      setError('This email is already assigned to this team');
      return;
    }

    // Create new user object
    const newUser = {
      id: `user_${Date.now()}`,
      email: email,
      exists_in_system: false // For now, assume all are new users
    };

    // Add user to the team
    setUserAssignments(prev => ({
      ...prev,
      [partyId]: {
        ...prev[partyId],
        [teamId]: [...(prev[partyId]?.[teamId] || []), newUser]
      }
    }));

    setError(null);
  };

  const handleSubmitEmail = async () => {
    if (!newUserEmail.trim()) return;

    const email = newUserEmail.trim();
    const { partyId, teamId } = showEmailForm;

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    // Check if email already exists in this team
    const existingUser = userAssignments[partyId]?.[teamId]?.find(u => u.email === email);
    if (existingUser) {
      setError('This email is already assigned to this team');
      return;
    }

    try {
      // Check if user exists in the system
      const { data: existingUserData } = await supabase
        .from('auth.users')
        .select('id, email')
        .eq('email', email)
        .single();

      const newUser = {
        id: `temp_${Date.now()}`,
        email: email,
        user_id: existingUserData?.id || null,
        exists_in_system: !!existingUserData,
        assigned_at: new Date().toISOString()
      };

      setUserAssignments(prev => ({
        ...prev,
        [partyId]: {
          ...prev[partyId],
          [teamId]: [...(prev[partyId]?.[teamId] || []), newUser]
        }
      }));

      setShowEmailForm(null);
      setNewUserEmail('');
      setError(null);
    } catch (err) {
      console.error('Error checking user:', err);
      setError('Failed to validate email address');
    }
  };

  const handleRemoveUser = (partyId, teamId, userId) => {
    setUserAssignments(prev => ({
      ...prev,
      [partyId]: {
        ...prev[partyId],
        [teamId]: prev[partyId][teamId].filter(u => u.id !== userId)
      }
    }));
  };

  const handleComplete = async () => {
    setLoading(true);
    setError(null);

    try {
      const allUsers = [];
      const savedUsers = [];
      
      // Collect all user assignments
      Object.keys(userAssignments).forEach(partyId => {
        Object.keys(userAssignments[partyId]).forEach(teamId => {
          userAssignments[partyId][teamId].forEach(user => {
            allUsers.push({
              ...user,
              party_id: partyId,
              team_id: teamId
            });
          });
        });
      });

      console.log('Saving user assignments to database:', allUsers);
      console.log('ProjectId:', projectId);

      // Save each user to dataroom_users with email (pending state)
      for (const userAssignment of allUsers) {
        try {
          console.log(`Attempting to save user: ${userAssignment.email}, party: ${userAssignment.party_id}, team: ${userAssignment.team_id}`);
          
          // Save to dataroom_users table with email (user_id will be null for pending users)
          const insertData = {
            email: userAssignment.email,
            user_id: null, // Will be populated when user accepts invite
            project_id: projectId,
            role: 'viewer',
            status: 'pending',
            invited_at: new Date().toISOString()
          };
          
          console.log('Insert data:', insertData);
          
          const { data: dataroomUser, error: userError } = await supabase
            .from('dataroom_users')
            .insert(insertData)
            .select()
            .single();

          if (userError) {
            // If user already exists for this project, skip
            if (userError.code === '23505') { // Unique violation
              console.log(`✓ User ${userAssignment.email} already exists for this project, updating metadata`);
              savedUsers.push(userAssignment);
              
              // Update metadata for existing user
              const { error: updateError } = await supabase
                .from('dataroom_users')
                .update({
                  metadata: {
                    party_id: userAssignment.party_id,
                    team_id: userAssignment.team_id === 'party_level' ? null : userAssignment.team_id,
                    first_name: userAssignment.firstName || null,
                    last_name: userAssignment.lastName || null,
                    added_via_wizard: true
                  }
                })
                .eq('email', userAssignment.email)
                .eq('project_id', projectId);
              
              if (updateError) {
                console.error('Failed to update metadata:', updateError);
              }
              continue;
            }
            console.error(`❌ Failed to add ${userAssignment.email} to dataroom_users:`, userError);
            console.error('Error details:', userError);
            continue;
          }
          
          console.log(`✓ Created user record for ${userAssignment.email}:`, dataroomUser);

          // Save party assignment with metadata for party_id and team_id
          const { error: metaError } = await supabase
            .from('dataroom_users')
            .update({
              metadata: {
                party_id: userAssignment.party_id,
                team_id: userAssignment.team_id === 'party_level' ? null : userAssignment.team_id,
                first_name: userAssignment.firstName || null,
                last_name: userAssignment.lastName || null,
                added_via_wizard: true
              }
            })
            .eq('email', userAssignment.email)
            .eq('project_id', projectId);

          if (metaError) {
            console.warn(`Failed to update metadata for ${userAssignment.email}:`, metaError);
          }

          console.log(`✓ Saved ${userAssignment.email} to dataroom_users`);
          savedUsers.push(userAssignment);
        } catch (err) {
          console.error('Error saving user assignment:', err);
        }
      }

      console.log(`Successfully saved ${savedUsers.length} of ${allUsers.length} user assignments`);
      
      onComplete({ 
        userAssignments: savedUsers,
        totalProcessed: allUsers.length,
        successCount: savedUsers.length
      });
    } catch (err) {
      console.error('Error completing user invitations:', err);
      setError(err.message || 'Failed to complete user invitations');
    } finally {
      setLoading(false);
    }
  };

  const getPartyName = (partyId) => {
    return setupData.parties?.find(p => p.id === partyId)?.name || 'Unknown Party';
  };

  const getPartyInfo = (partyId) => {
    return setupData.parties?.find(p => p.id === partyId) || { name: 'Unknown Party', color: '#6B7280', code: 'UNK' };
  };

  const getTeamName = (partyId, teamId) => {
    return setupData.teams?.[partyId]?.find(t => t.id === teamId)?.name || 'Unknown Team';
  };

  const totalUsers = Object.values(userAssignments).reduce((total, party) => {
    return total + Object.values(party).reduce((partyTotal, team) => {
      return partyTotal + team.length;
    }, 0);
  }, 0);

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Email Form Modal */}
      {showEmailForm && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Add User to Team
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Add a user to <strong>{getTeamName(showEmailForm.partyId, showEmailForm.teamId)}</strong> in <strong>{getPartyName(showEmailForm.partyId)}</strong>
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={newUserEmail}
                  onChange={(e) => setNewUserEmail(e.target.value)}
                  placeholder="user@example.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  autoFocus
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowEmailForm(null);
                  setNewUserEmail('');
                  setError(null);
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitEmail}
                disabled={!newUserEmail.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                Add User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Parties and Teams */}
      <div className="space-y-4">
        {setupData.parties?.map((party) => {
          const partyTeams = setupData.teams?.[party.id] || [];
          const isExpanded = expandedParty === party.id;
          const partyInfo = getPartyInfo(party.id);
          
          return (
            <div key={party.id} className="border border-gray-200 rounded-lg overflow-hidden">
              {/* Party Header - Clickable to expand/collapse */}
              <div 
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => togglePartyExpansion(party.id)}
                style={{ backgroundColor: `${partyInfo.color}15` }}
              >
                <div className="flex items-center">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                    {isExpanded ? (
                      <ChevronDown className="w-5 h-5" style={{ color: partyInfo.color }} />
                    ) : (
                      <ChevronRight className="w-5 h-5" style={{ color: partyInfo.color }} />
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-4 h-4 rounded-full flex-shrink-0"
                      style={{ backgroundColor: partyInfo.color }}
                    />
                    <span 
                      className="px-2 py-1 text-xs font-bold rounded"
                      style={{ 
                        backgroundColor: partyInfo.color,
                        color: 'white'
                      }}
                    >
                      {partyInfo.code}
                    </span>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{partyInfo.name}</h3>
                      <p className="text-sm text-gray-500">Add users to teams within this party</p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">
                    {partyTeams.length} team{partyTeams.length !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>

              {/* Teams Section - Collapsible */}
              {isExpanded && (
                <div className="border-t border-gray-200 p-4 bg-gray-50">
                  {/* Teams */}
                  <div className="space-y-3">
                    {partyTeams.map((team) => {
                      const teamUsers = userAssignments[party.id]?.[team.id] || [];
                      
                      return (
                        <div key={team.id} className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center">
                              <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                              <h4 className="font-medium text-gray-900">{team.name}</h4>
                              <span className="ml-2 text-sm text-gray-500">
                                ({teamUsers.length} user{teamUsers.length !== 1 ? 's' : ''})
                              </span>
                            </div>
                          </div>

                          {/* Inline Email Input */}
                          <div className="flex gap-2 mb-3">
                            <input
                              type="email"
                              placeholder="Enter email address"
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  const email = e.target.value.trim();
                                  if (email) {
                                    handleAddUserInline(party.id, team.id, email);
                                    e.target.value = '';
                                  }
                                }
                              }}
                            />
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                const input = e.target.previousElementSibling;
                                const email = input.value.trim();
                                if (email) {
                                  handleAddUserInline(party.id, team.id, email);
                                  input.value = '';
                                }
                              }}
                              className="flex items-center px-3 py-2 text-sm text-white rounded-lg hover:opacity-80 transition-opacity"
                              style={{ backgroundColor: partyInfo.color }}
                            >
                              <Plus className="w-4 h-4 mr-2" />
                              Add
                            </button>
                          </div>

                          {/* Users in this team */}
                          {teamUsers.length > 0 ? (
                            <div className="space-y-2">
                              {teamUsers.map((user) => (
                                <div key={user.id} className="flex items-center justify-between bg-gray-50 rounded-lg p-3 border border-gray-200">
                                  <div className="flex items-center">
                                    <User className="w-4 h-4 text-gray-500 mr-2" />
                                    <div>
                                      <p className="text-sm font-medium text-gray-900">{user.email}</p>
                                      <p className="text-xs text-gray-500">
                                        {user.exists_in_system ? 'Existing user' : 'New user'}
                                      </p>
                                    </div>
                                  </div>
                                  <button
                                    onClick={() => handleRemoveUser(party.id, team.id, user.id)}
                                    className="text-gray-400 hover:text-red-600 transition-colors"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-4 border-2 border-dashed border-gray-300 rounded-lg">
                              <Mail className="w-6 h-6 text-gray-400 mx-auto mb-2" />
                              <p className="text-sm text-gray-500">No users added yet</p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Summary */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h4 className="text-md font-medium text-gray-900 mb-3">
          User Invitation Summary
        </h4>
        <div className="space-y-2">
          {setupData.parties?.map((party) => {
            const partyUsers = Object.values(userAssignments[party.id] || {}).reduce((total, team) => total + team.length, 0);
            const partyInfo = getPartyInfo(party.id);
            
            return (
              <div key={party.id} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div 
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: partyInfo.color }}
                  />
                  <span className="text-gray-900 font-medium">{partyInfo.name}</span>
                  <span 
                    className="px-2 py-1 text-xs font-bold rounded"
                    style={{ 
                      backgroundColor: partyInfo.color,
                      color: 'white'
                    }}
                  >
                    {partyInfo.code}
                  </span>
                </div>
                <span className="text-gray-700">
                  {partyUsers} user{partyUsers !== 1 ? 's' : ''}
                </span>
              </div>
            );
          })}
          <div className="border-t border-gray-200 pt-2 mt-2">
            <div className="flex items-center justify-between">
              <span className="text-gray-900 font-medium">Total Users to Invite</span>
              <span className="text-gray-700 font-semibold">{totalUsers}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Complete Button */}
      <div className="flex justify-end pt-6 border-t">
        <button
          onClick={handleComplete}
          disabled={loading}
          className="flex items-center px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? (
            <>
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Completing Setup...
            </>
          ) : (
            <>
              <CheckCircle className="w-5 h-5 mr-2" />
              Complete Setup
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default UserInvitationStep;
